import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Download, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ANGLES = [
  ["product_hype", "Product Hype"],
  ["founders_offer", "Intro Offer"],
  ["performance", "Performance"],
  ["sleep", "Sleep"],
  ["ugc", "UGC Style"],
];

const SocialStudio = () => {
  const [angle, setAngle] = useState("product_hype");
  const [note, setNote] = useState("");
  const [generating, setGenerating] = useState(false);
  const [posts, setPosts] = useState([]);

  const loadPosts = async () => {
    try {
      const { data } = await axios.get(`${API}/social/posts`, { withCredentials: true });
      setPosts(data.posts);
    } catch {
      /* history optional */
    }
  };

  useEffect(() => {
    loadPosts();
  }, []);

  const onGenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(
        `${API}/social/generate`,
        { angle, note },
        { withCredentials: true, timeout: 120000 }
      );
      setPosts((p) => [data, ...p]);
      toast.success("Ad generated — download it below.");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Generation failed — try again.");
    } finally {
      setGenerating(false);
    }
  };

  const onCopy = (caption) => {
    navigator.clipboard.writeText(caption).then(
      () => toast.success("Caption copied."),
      () => toast.error("Copy failed — select the text manually.")
    );
  };

  return (
    <div data-testid="social-studio" className="mt-10 border border-white/10 bg-[#121212] p-6 md:p-8">
      <p className="font-anton uppercase text-xl md:text-2xl flex items-center gap-3">
        <Sparkles size={22} className="text-volt" /> Social studio
      </p>
      <p className="mt-2 text-white/60 font-light text-sm md:text-base max-w-xl">
        Generate branded, one-of-a-kind ad creatives for Facebook and Instagram. Every image is
        unique; every caption is written to match. Download and post.
      </p>

      <div className="mt-6 flex flex-wrap gap-2">
        {ANGLES.map(([key, label]) => (
          <button
            key={key}
            data-testid={`social-angle-${key}`}
            onClick={() => setAngle(key)}
            className={`px-5 h-11 font-anton uppercase tracking-wider text-sm transition-[background-color,color,border-color,transform] duration-200 active:scale-95 ${
              angle === key
                ? "bg-volt text-black"
                : "border border-white/20 text-white/60 hover:text-white hover:border-white/50"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="mt-5 flex flex-col md:flex-row gap-3">
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="OPTIONAL DIRECTION — E.G. 'ADD MARATHON VIBES'"
          data-testid="social-note-input"
          className="flex-1 h-12 bg-transparent border border-white/20 focus:border-volt px-4 font-plex text-sm uppercase tracking-[0.1em] text-white placeholder:text-white/30 outline-none transition-colors duration-300"
        />
        <button
          onClick={onGenerate}
          disabled={generating}
          data-testid="social-generate-button"
          className="h-12 px-8 bg-volt text-black font-anton uppercase tracking-wider text-sm transition-[background-color,transform] duration-200 hover:bg-volt-dark active:scale-95 disabled:opacity-60 shrink-0"
        >
          {generating ? "Generating — up to 60s…" : "Generate Ad"}
        </button>
      </div>

      {posts.length > 0 && (
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((p, i) => (
            <div
              key={p.id}
              data-testid={`social-post-card-${i + 1}`}
              className="border border-white/10 bg-[#0A0A0A] flex flex-col"
            >
              <img
                src={`${API}/social/image/${p.id}`}
                alt={`Breethe ${p.angle} ad`}
                data-testid={`social-image-${i + 1}`}
                className="w-full aspect-square object-cover"
              />
              <div className="p-5 flex flex-col flex-1">
                <p
                  data-testid={`social-caption-${i + 1}`}
                  className="text-white/70 font-light text-sm whitespace-pre-wrap flex-1"
                >
                  {p.caption}
                </p>
                <div className="mt-4 pt-4 border-t border-white/10 flex gap-2">
                  <a
                    href={`${API}/social/image/${p.id}`}
                    download={`breethe-${p.angle}-${p.id.slice(0, 8)}.png`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`social-download-${i + 1}`}
                    className="flex-1 h-10 flex items-center justify-center gap-2 bg-volt text-black font-anton uppercase text-xs tracking-wider transition-[background-color,transform] duration-200 hover:bg-volt-dark active:scale-95"
                  >
                    <Download size={14} /> Download
                  </a>
                  <button
                    onClick={() => onCopy(p.caption)}
                    data-testid={`social-copy-${i + 1}`}
                    className="flex-1 h-10 flex items-center justify-center gap-2 border border-white/25 text-white font-anton uppercase text-xs tracking-wider transition-[background-color,color,transform] duration-200 hover:bg-white hover:text-black active:scale-95"
                  >
                    <Copy size={14} /> Copy Caption
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SocialStudio;
